import { PencilSimple, Trash } from "@phosphor-icons/react";

const money = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const groupByCategory = (items) => {
  const map = new Map();
  for (const s of items) {
    const key = s.category || "Uncategorised";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(s);
  }
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
};

const GroupHeader = ({ name, count }) => (
  <div
    className="mb-2 flex items-center gap-3"
    data-testid={`services-group-${slug(name)}`}
  >
    <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-indigo-300">
      {name}
    </span>
    <span className="font-mono-data text-[11px] text-zinc-500">{count}</span>
    <div className="h-px flex-1 bg-zinc-800" />
  </div>
);

const Price = ({ s, compact }) => (
  <span
    className={`shrink-0 whitespace-nowrap font-mono-data font-bold text-indigo-300 ${
      compact ? "text-xs" : "text-sm"
    }`}
  >
    {money(s.cost_estimate)}
    <span className="ml-1 text-[10px] font-normal text-zinc-500">/{s.unit}</span>
  </span>
);

const Actions = ({ s, compact, onEdit, onDelete }) => {
  const size = compact ? "h-7 w-7" : "h-8 w-8";
  return (
    <div className="flex shrink-0 items-center gap-1">
      <button
        type="button"
        data-testid={`edit-service-${s.id}`}
        title="Edit"
        onClick={() => onEdit(s)}
        className={`${size} inline-flex items-center justify-center rounded-md border border-zinc-800 text-zinc-300 transition-colors duration-150 hover:border-indigo-400/50 hover:bg-zinc-800 hover:text-white`}
      >
        <PencilSimple size={13} weight="bold" />
      </button>
      <button
        type="button"
        data-testid={`delete-service-${s.id}`}
        title="Delete"
        onClick={() => onDelete(s)}
        className={`${size} inline-flex items-center justify-center rounded-md border border-zinc-800 text-zinc-500 transition-colors duration-150 hover:border-red-400/40 hover:bg-zinc-800 hover:text-red-400`}
      >
        <Trash size={13} weight="bold" />
      </button>
    </div>
  );
};

export const ServiceRows = ({ groups, compact, onEdit, onDelete }) => (
  <div className="mt-4 space-y-5" data-testid="services-list">
    {groups.map(([name, list]) => (
      <section key={name}>
        <GroupHeader name={name} count={list.length} />
        <div className="divide-y divide-zinc-800/80 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900">
          {list.map((s) => (
            <div
              key={s.id}
              data-testid={`service-row-${s.id}`}
              className={`flex items-center gap-3 px-3 transition-colors duration-150 hover:bg-zinc-800/40 sm:px-4 ${
                compact ? "h-10" : "h-14"
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-baseline gap-2">
                  <span
                    className={`truncate font-semibold ${compact ? "text-[13px]" : "text-sm"}`}
                  >
                    {s.name}
                  </span>
                  {s.type && (
                    <span className="hidden truncate text-[11px] text-zinc-500 sm:inline">
                      {s.type}
                    </span>
                  )}
                </div>
                {!compact && s.description && (
                  <p className="truncate text-[11px] text-zinc-500">
                    {s.description}
                  </p>
                )}
              </div>
              <Price s={s} compact={compact} />
              <Actions s={s} compact={compact} onEdit={onEdit} onDelete={onDelete} />
            </div>
          ))}
        </div>
      </section>
    ))}
  </div>
);

export const ServiceCards = ({ groups, compact, onEdit, onDelete }) => (
  <div className="mt-4 space-y-6" data-testid="services-cards">
    {groups.map(([name, list]) => (
      <section key={name}>
        <GroupHeader name={name} count={list.length} />
        <div
          className={
            compact
              ? "grid gap-2 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
              : "grid gap-3 sm:grid-cols-2 xl:grid-cols-3"
          }
        >
          {list.map((s) => (
            <div
              key={s.id}
              data-testid={`service-card-${s.id}`}
              className={`rounded-lg border border-zinc-800 bg-zinc-900 transition-colors duration-150 hover:border-indigo-400/40 ${
                compact ? "p-3" : "p-4"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3
                    className={`truncate font-bold ${compact ? "text-sm" : "text-base"}`}
                  >
                    {s.name}
                  </h3>
                  {s.type && (
                    <p className="truncate text-[11px] text-zinc-500">{s.type}</p>
                  )}
                </div>
                <Actions s={s} compact={compact} onEdit={onEdit} onDelete={onDelete} />
              </div>
              {!compact && s.description && (
                <p className="mt-2 line-clamp-2 text-xs text-zinc-400">
                  {s.description}
                </p>
              )}
              <div className={compact ? "mt-2" : "mt-3"}>
                <Price s={s} compact={compact} />
              </div>
            </div>
          ))}
        </div>
      </section>
    ))}
  </div>
);
